import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "../../components/feature/Header";
import Footer from "../../components/feature/Footer";
import SEO from "../../components/base/SEO";
import { useTranslation } from "react-i18next";

interface Article {
  id: string;
  slug: string;
  title: string;
  titleEn: string;
  description: string;
  descriptionEn: string;
  category: string;
  tags: string[];
  author: string;
  publishDate: string;
  readTime: string;
  thumbnail: string;
  htmlFile: string;
}

export default function Blog() {
  const { t } = useTranslation();
  const isZh = t("nav.language") === "中";
  const [articles, setArticles] = useState<Article[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");

  useEffect(() => {
    fetch("/blog/articles.json")
      .then((res) => res.json())
      .then((data) => setArticles(data.articles))
      .catch((err) => console.error("Failed to load articles:", err));
  }, []);

  const categories = ["all", ...new Set(articles.map((a) => a.category))];

  const filteredArticles =
    selectedCategory === "all"
      ? articles
      : articles.filter((a) => a.category === selectedCategory);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="物流知識庫 | AUTOCHAIN"
        description="分享 WMS 倉庫管理、TMS 運輸管理、物流數碼轉型的專業知識和實戰經驗，幫助香港企業提升物流效率。"
        keywords="WMS, TMS, 倉庫管理, 運輸管理, 物流知識, 香港物流, 數碼轉型"
        canonicalUrl="/knowledge"
      />
      
      {/* Structured Data - Blog */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Blog",
        "name": "AUTOCHAIN 物流知識庫",
        "description": "分享 WMS 倉庫管理、TMS 運輸管理、物流數碼轉型的專業知識和實戰經驗",
        "url": "https://autochain.com.hk/knowledge",
        "publisher": {
          "@type": "Organization",
          "name": "AUTOCHAIN TECHNOLOGY",
          "logo": {
            "@type": "ImageObject",
            "url": "https://autochain.com.hk/logo.png"
          }
        }
      })}} />
      
      {/* Structured Data - Breadcrumb */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "首頁", "item": "https://autochain.com.hk/" },
          { "@type": "ListItem", "position": 2, "name": "物流知識庫", "item": "https://autochain.com.hk/knowledge" }
        ]
      })}} />
      
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {isZh ? "物流知識庫" : "Logistics Knowledge Hub"}
          </h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">
            {isZh
              ? "分享 WMS、TMS 和物流管理的專業知識，幫助你的業務更上一層樓"
              : "Sharing professional knowledge on WMS, TMS and logistics management to help your business grow"}
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-gray-50 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === cat
                    ? "bg-blue-600 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-100"
                }`}
              >
                {cat === "all" ? (isZh ? "全部文章" : "All Articles") : cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredArticles.length === 0 ? (
            <div className="text-center py-12">
              <i className="ri-article-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-gray-500">
                {isZh ? "暫無文章" : "No articles yet"}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/knowledge/${article.slug}`}
                  className="bg-white rounded-2xl shadow-sm hover:shadow-lg transition-shadow overflow-hidden group"
                >
                  <div className="aspect-video bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center overflow-hidden">
                    {article.thumbnail ? (
                      <img
                        src={article.thumbnail}
                        alt={isZh ? article.title : article.titleEn}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <i className="ri-article-line text-5xl text-blue-400"></i>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                        {article.category}
                      </span>
                      <span className="text-gray-400 text-xs">
                        {article.readTime}
                      </span>
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors line-clamp-2">
                      {isZh ? article.title : article.titleEn}
                    </h2>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                      {isZh ? article.description : article.descriptionEn}
                    </p>
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <span>{article.author}</span>
                      <span>{article.publishDate}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            {isZh ? "想了解更多？" : "Want to Learn More?"}
          </h2>
          <p className="text-gray-600 mb-8">
            {isZh
              ? "預約免費諮詢，讓我們的專家為你分析最適合的物流解決方案"
              : "Book a free consultation and let our experts analyze the best logistics solution for you"}
          </p>
          <button
            onClick={() => {
              const message = encodeURIComponent(
                isZh
                  ? "您好！我想了解 AUTOCHAIN 的物流解決方案。"
                  : "Hello! I would like to learn more about AUTOCHAIN's logistics solutions."
              );
              window.open(`https://wa.me/85294252903?text=${message}`, "_blank");
            }}
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            <i className="ri-whatsapp-line text-xl mr-2"></i>
            {isZh ? "WhatsApp 聯絡我們" : "Contact Us on WhatsApp"}
          </button>
        </div>
      </section>

      {/* Knowledge Hub Introduction */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none text-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {isZh ? "關於物流知識庫" : "About Our Knowledge Hub"}
            </h2>
            <p className="mb-4">
              {isZh
                ? "歡迎來到 AUTOCHAIN 物流知識庫！我們深明香港中小企業在物流管理上面對的種種挑戰——從倉庫空間有限、人手不足，到運輸成本高昂、客戶要求即日送達等壓力。因此，我們建立了這個知識庫，希望透過分享實用的物流知識和行業經驗，幫助你的業務更上一層樓。"
                : "Welcome to the AUTOCHAIN Logistics Knowledge Hub! We understand the various challenges that Hong Kong SMEs face in logistics management - from limited warehouse space and manpower shortages to high transportation costs and customer demands for same-day delivery. That's why we created this knowledge hub to share practical logistics knowledge and industry experience to help your business grow."}
            </p>
            <p className="mb-4">
              {isZh
                ? "在這裡，你可以找到關於 WMS 倉庫管理系統、TMS 運輸管理系統、物流數碼轉型、成本控制策略等主題的深度文章。我們的內容由擁有多年物流行業經驗的專家撰寫，結合香港本地市場的實際情況，提供切實可行的建議和解決方案。"
                : "Here you can find in-depth articles on topics such as WMS warehouse management systems, TMS transportation management systems, logistics digital transformation, and cost control strategies. Our content is written by experts with years of logistics industry experience, combined with the actual situation of the Hong Kong local market, providing practical advice and solutions."}
            </p>
            <p>
              {isZh
                ? "無論你是剛開始接觸物流系統的新手，還是想優化現有流程的資深從業者，我們都希望這些文章能為你帶來啟發和幫助。如果你有任何問題或想了解更多，歡迎隨時聯絡我們的團隊。"
                : "Whether you're a newcomer just starting to learn about logistics systems or a seasoned professional looking to optimize existing processes, we hope these articles will inspire and help you. If you have any questions or want to learn more, feel free to contact our team anytime."}
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
